import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Mail, MapPin, MessageCircle, Send, Check } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WHATSAPP_NUMBER = '+919876543210';
const WHATSAPP_MESSAGE = 'Hi! I would like to inquire about your services.';

const BUSINESS_INFO = {
  whatsapp: '+919876543210',
  email: 'mail@drycleanspace.com',
  phone: '+91 98765 43210',
  address: 'Rohini Sector 11, New Delhi',
};

const SERVICES_OPTIONS = [
  { value: 'shoe-cleaning', label: 'Shoe Cleaning' },
  { value: 'shoe-repair', label: 'Shoe Repair' },
  { value: 'sneaker-restoration', label: 'Sneaker Restoration' },
  { value: 'handbag-cleaning', label: 'Handbag Cleaning & Repair' },
  { value: 'other', label: 'Other / Custom Request' },
];

const FadeInSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setIsSubmitted(true);
      toast.success('Your inquiry has been submitted! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit. Please try again or contact us via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-[#F5F0E6]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
              Get In Touch
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A1A1A] mb-6">
              Contact Us
            </h1>
            <p className="font-body text-base md:text-lg text-[#66635B] max-w-2xl mx-auto">
              Have a question or ready to book? Reach out to us via WhatsApp for instant response, or fill out the form below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <FadeInSection>
              <div>
                <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                  Contact Information
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A] mb-8">
                  Let's Connect
                </h2>

                {/* WhatsApp CTA */}
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-cta"
                  className="flex items-center gap-4 p-6 bg-[#25D366]/10 border border-[#25D366]/30 mb-8 hover:bg-[#25D366]/20 transition-colors group"
                >
                  <div className="w-14 h-14 bg-[#25D366] flex items-center justify-center flex-shrink-0">
                    <MessageCircle size={28} className="text-white" />
                  </div>
                  <div>
                    <p className="font-body text-sm text-[#25D366] uppercase tracking-wider mb-1">
                      Instant Response
                    </p>
                    <p className="font-heading text-xl font-semibold text-[#1A1A1A] group-hover:text-[#25D366] transition-colors">
                      Chat on WhatsApp
                    </p>
                  </div>
                </a>

                {/* Contact Details */}
                <div className="space-y-6">
                  <a
                    href={`mailto:${BUSINESS_INFO.email}`}
                    data-testid="contact-email"
                    className="flex items-start gap-4 p-4 bg-[#FAFAFA] border border-[#EAE5D9] hover:border-[#C5A059] transition-colors"
                  >
                    <div className="w-10 h-10 border border-[#C5A059] flex items-center justify-center flex-shrink-0">
                      <Mail size={18} className="text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="font-body text-xs text-[#66635B] uppercase tracking-wider mb-1">Email</p>
                      <p className="font-body text-base text-[#1A1A1A]">{BUSINESS_INFO.email}</p>
                    </div>
                  </a>

                  <a
                    href={`tel:${BUSINESS_INFO.phone.replace(/\s/g, '')}`}
                    data-testid="contact-phone"
                    className="flex items-start gap-4 p-4 bg-[#FAFAFA] border border-[#EAE5D9] hover:border-[#C5A059] transition-colors"
                  >
                    <div className="w-10 h-10 border border-[#C5A059] flex items-center justify-center flex-shrink-0">
                      <Phone size={18} className="text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="font-body text-xs text-[#66635B] uppercase tracking-wider mb-1">Phone</p>
                      <p className="font-body text-base text-[#1A1A1A]">{BUSINESS_INFO.phone}</p>
                    </div>
                  </a>

                  <div
                    data-testid="contact-address"
                    className="flex items-start gap-4 p-4 bg-[#FAFAFA] border border-[#EAE5D9]"
                  >
                    <div className="w-10 h-10 border border-[#C5A059] flex items-center justify-center flex-shrink-0">
                      <MapPin size={18} className="text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="font-body text-xs text-[#66635B] uppercase tracking-wider mb-1">Location</p>
                      <p className="font-body text-base text-[#1A1A1A]">{BUSINESS_INFO.address}</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInSection>

            {/* Contact Form */}
            <FadeInSection delay={0.2}>
              <div className="bg-[#FAFAFA] border border-[#EAE5D9] p-8 md:p-10">
                <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                  Inquiry Form
                </span>
                <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-8">
                  Send Us a Message
                </h2>

                {isSubmitted ? (
                  <div data-testid="form-success" className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-6 bg-[#C5A059]/10 border border-[#C5A059] flex items-center justify-center">
                      <Check size={32} className="text-[#C5A059]" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-2">
                      Thank You!
                    </h3>
                    <p className="font-body text-sm text-[#66635B] mb-6">
                      Your inquiry has been submitted. We'll get back to you within 24 hours.
                    </p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="btn-secondary"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                    <div>
                      <label className="font-body text-xs uppercase tracking-wider text-[#66635B] mb-2 block">
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="input-name"
                        className="w-full bg-white border-[#EAE5D9] rounded-none focus:border-[#C5A059] focus:ring-0"
                        placeholder="Your name"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="font-body text-xs uppercase tracking-wider text-[#66635B] mb-2 block">
                          Email *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          data-testid="input-email"
                          className="w-full bg-white border-[#EAE5D9] rounded-none focus:border-[#C5A059] focus:ring-0"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label className="font-body text-xs uppercase tracking-wider text-[#66635B] mb-2 block">
                          Phone *
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          data-testid="input-phone"
                          className="w-full bg-white border-[#EAE5D9] rounded-none focus:border-[#C5A059] focus:ring-0"
                          placeholder="+91 98765 43210"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="font-body text-xs uppercase tracking-wider text-[#66635B] mb-2 block">
                        Service Required *
                      </label>
                      <Select value={formData.service} onValueChange={handleServiceChange} required>
                        <SelectTrigger
                          data-testid="select-service"
                          className="w-full bg-white border-[#EAE5D9] rounded-none focus:border-[#C5A059] focus:ring-0"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICES_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="font-body text-xs uppercase tracking-wider text-[#66635B] mb-2 block">
                        Message *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        data-testid="input-message"
                        className="w-full bg-white border-[#EAE5D9] rounded-none focus:border-[#C5A059] focus:ring-0 resize-none"
                        placeholder="Describe your items and requirements..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="submit-button"
                      className="w-full bg-[#1A1A1A] text-white rounded-none py-6 font-body text-sm uppercase tracking-wider hover:bg-[#333333] disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Inquiry
                          <Send size={16} className="ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
